import type { WorkspacePlanTier } from '@/types/session'

/**
 * Centralized tier semantics (PDR 011 / #4208, decomposition §9a).
 *
 * `WorkspacePlanTier` is `'free' | 'pro' | 'team' | 'trial'`, and `trial` is a
 * FIRST-CLASS tier that must be classified explicitly at every branch — a bare
 * `=== 'free'` / `pro||team` check silently lumps `trial` onto one side and reads
 * as "handled" when it isn't. These predicates make the intent legible and keep
 * the classification in one place. `null`/`undefined` (tier unknown / still
 * loading) is never paid, free, trial, or upgradeable.
 */

/** A PAID tier — an active subscription (Pro or Team). `trial` is NOT paid: it
 *  grants the free feature set, time-limited. */
export function isPaidTier(tier: WorkspacePlanTier | null | undefined): boolean {
  return tier === 'pro' || tier === 'team'
}

/** The free (non-trial) tier. */
export function isFreeTier(tier: WorkspacePlanTier | null | undefined): boolean {
  return tier === 'free'
}

/** The time-limited trial tier (PDR 011 D22). Distinct from `free`. */
export function isTrialTier(tier: WorkspacePlanTier | null | undefined): boolean {
  return tier === 'trial'
}

/** An UNPAID, upgradeable tier — `free` OR `trial`. The set that converts via
 *  upgrade-in-place (PDR 011 §7). */
export function isUpgradeableTier(
  tier: WorkspacePlanTier | null | undefined,
): boolean {
  return tier === 'free' || tier === 'trial'
}
